import { FC, useEffect, useState } from 'react';
import { useAppDrawer } from '../../../../../AppDrawer/AppDrawerProvider';
import { DomainType, LocalAccount } from '../../../../types/LocalAccount';
import { EntityChip, Icons } from '@silverfort/sf-components';
import CopyIcon from '../../../../../Controls/Buttons/CopyIcon';
import { getDateShortFormat } from '../../../../../../utils/dates';
import useGetLocalAccountsBoundUsers from '../../../../api/queries/useGetLocalAccountsBoundUsers';
import ContentHeader from './ContentHeader';
import BoundUsersTable from './BoundAccountsTable';
import { UserSession } from '../../../../../../api';
import { DOMAIN_TYPE_TO_LABEL, LOCAL_ACCOUNT_TYPE_TO_LABEL } from '../../consts';
import { SnackAlertContextValue } from '../../../../context/SnackAlertProvider';

interface LocalAccountDrawerProps {
    localAccount: LocalAccount;
    userSession: UserSession;
    onAccountUpdated: () => void;
    hasAccountMatching: boolean;
    snackAlertContext: SnackAlertContextValue;
}

const LocalAccountDrawer: FC<LocalAccountDrawerProps> = ({
    localAccount,
    userSession,
    onAccountUpdated,
    hasAccountMatching,
    snackAlertContext
}) => {
    const [boundAdEntityGuid, setBoundAdEntityGuid] = useState<string | undefined>(localAccount.boundAdEntity?.guid);
    const {
        boundUsers,
        loading: isBoundUsersLoading,
        error: boundUsersError
    } = useGetLocalAccountsBoundUsers(boundAdEntityGuid);
    const { updateAppDrawerProps } = useAppDrawer();

    useEffect(() => {
        updateAppDrawerProps({
            headerTitle: 'Manage Account'
        });
    }, []);

    useEffect(() => {
        setBoundAdEntityGuid(localAccount.boundAdEntity?.guid);
    }, [localAccount.boundAdEntity?.guid]);

    return (
        <div className="flex flex-col h-[calc(100vh-65px)]">
            <ContentHeader
                localAccount={localAccount}
                userSession={userSession}
                onAccountUpdated={onAccountUpdated}
                snackAlertContext={snackAlertContext}
            />
            <div className="flex flex-col divide-y-2 divide-dashed text-13/[16px]">
                <div className="flex gap-8 py-2 pl-8 pr-6">
                    <div className="h-20 flex flex-col gap-2 justify-center">
                        <div className="text-sf-gray-500">Device name</div>
                        <div>
                            <EntityChip className="text-13/[16px]" text={localAccount.hostname} icon={Icons.Computer} />
                        </div>
                    </div>
                    <div className="h-20 flex flex-col gap-2 justify-center">
                        <div className="text-sf-gray-500">Domain type</div>
                        <div className="h-6 flex items-center">{DOMAIN_TYPE_TO_LABEL[localAccount.domainType]}</div>
                    </div>
                    {localAccount.domainType === DomainType.ENTRA_ID && (
                        <div className="h-20 flex flex-col gap-2 justify-center">
                            <div className="text-sf-gray-500">Device ID</div>
                            <div className="h-6 flex items-center gap-1">
                                <div>{localAccount.deviceId}</div>
                                <CopyIcon value={localAccount.deviceId} />
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex gap-8 py-2 pl-8 pr-6">
                    <div className="h-20 flex flex-col gap-2 justify-center">
                        <div className="text-sf-gray-500">Last logon</div>
                        <div className="h-6 flex items-center">
                            {localAccount.lastLogon ? getDateShortFormat(localAccount.lastLogon) : 'N/A'}
                        </div>
                    </div>
                    <div className="h-20 flex flex-col gap-2 justify-center">
                        <div className="text-sf-gray-500">Account type</div>
                        <div className="h-6 flex items-center">
                            {LOCAL_ACCOUNT_TYPE_TO_LABEL[localAccount.accountType]}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex px-6">
                <BoundUsersTable
                    isLoading={isBoundUsersLoading}
                    error={boundUsersError}
                    boundAccounts={boundUsers ?? []}
                    hasAccountMatching={hasAccountMatching}
                />
            </div>
        </div>
    );
};

export default LocalAccountDrawer;
